import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { PayloadAction } from '@reduxjs/toolkit';
import { axiosInstance } from '../../shared/api/axiosInstance';
import axios from 'axios';
import { AxiosError } from 'axios';
import { TrendingTopicsResponse, TrendingTopic } from '../../shared/types/topicTypes';

export const getSuggestions = createAsyncThunk<any, string, { rejectValue: string }>(
    'topics/getSuggestions',
    async (search: string, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/topics/suggestions/?search=${search}`);
            const transformedData = response.data.data.map((item: any) => item.attributes.title);
            return transformedData;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(
                    axiosError.response?.data.message || 'Failed to fetch suggestions',
                );
            }
            return rejectWithValue('An unexpected error occurred while fetching suggestions');
        }
    },
);

export const getTrendingTopics = createAsyncThunk<TrendingTopic[], string, { rejectValue: string }>(
    'topics/getTrendingTopics',
    async (search: string, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get<TrendingTopicsResponse>(
                `/topics/trends?search=${search}`,
            );
            const transformedData = response.data.data.map((item) => ({
                id: item.id,
                key: item.attributes.key,
                title: item.attributes.title,
            }));
            return transformedData;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(
                    axiosError.response?.data.message || 'Failed to fetch trending topics',
                );
            }
            return rejectWithValue('An unexpected error occurred while fetching trending topics');
        }
    },
);

type OnboardingState = {
    kind: 'text_only' | 'text_image' | 'image_only' | 'multi_image' | null;
    goal: string | null;
    tone: string | null;
    industry: string | null;
    content_type: string | null;
    suggestions: string[] | null;
    trendingTopics: TrendingTopic[] | null;
    isLoadingTrendingTopics: boolean;
};

export const initialState: OnboardingState = {
    kind: null,
    goal: null,
    tone: null,
    industry: null,
    content_type: null,
    suggestions: null,
    trendingTopics: null,
    isLoadingTrendingTopics: false,
};

const onboardingSlice = createSlice({
    name: 'onboarding',
    initialState,
    reducers: {
        setKind(
            state,
            action: PayloadAction<'text_only' | 'text_image' | 'image_only' | 'multi_image' | null>,
        ) {
            state.kind = action.payload;
        },
        setGoal(state, action: PayloadAction<string>) {
            state.goal = action.payload;
        },
        setTone(state, action: PayloadAction<string>) {
            state.tone = action.payload;
        },
        setContentType(state, action: PayloadAction<string>) {
            state.content_type = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSuggestions.fulfilled, (state, action) => {
                state.suggestions = action.payload;
            })
            .addCase(getTrendingTopics.pending, (state) => {
                state.isLoadingTrendingTopics = true;
            })
            .addCase(getTrendingTopics.fulfilled, (state, action) => {
                state.trendingTopics = action.payload;
                state.isLoadingTrendingTopics = false;
            })
            .addCase(getTrendingTopics.rejected, (state) => {
                state.isLoadingTrendingTopics = false;
            });
    },
});

export const { setKind, setGoal, setTone, setContentType } = onboardingSlice.actions;

export default onboardingSlice.reducer;
